/**
 * The newsletter email, as one HTML string.
 *
 * The same function renders the builder preview, the gallery thumbnails, the
 * media kit and the email that is actually sent, so what the creator lays out
 * is what lands in the inbox. Tables and inline styles only, at 600px, with
 * email-safe fonts: anything cleverer breaks in Outlook or Gmail.
 */

export type Family = 'letter' | 'bulletin' | 'magazine'
export type AdShape = 'banner' | 'side' | 'square' | 'leaderboard'

export const SHAPES: Record<AdShape, { w: number; h: number; label: string }> = {
  banner: { w: 600, h: 150, label: 'Banner 600×150' },
  side: { w: 280, h: 240, label: 'Side 280×240' },
  square: { w: 300, h: 250, label: 'Square 300×250' },
  leaderboard: { w: 600, h: 100, label: 'Leaderboard 600×100' },
}

export const EMAIL_FONTS = ['Georgia', 'Arial', 'Helvetica', 'Verdana', 'Trebuchet MS', 'Times New Roman', 'Courier New']

export const FAMILY_INFO: Record<Family, { name: string; stories: [number, number]; ads: string; line: string }> = {
  letter: { name: 'The Letter', stories: [3, 6], ads: 'No ads at all', line: 'Text first, opening with your note. Personal, and nothing sold.' },
  bulletin: { name: 'The Bulletin', stories: [4, 10], ads: 'Fixed banner space', line: 'Sectioned like a local paper, with banner slots between sections.' },
  magazine: { name: 'The Magazine', stories: [4, 12], ads: 'Custom ad space', line: 'Laid out row by row. Ads only where you lock them.' },
}

export interface BrandKit {
  id?: string
  name: string
  /** header: the masthead's colour (Brian's Brand studio › Header › Color, log
   *  #008, Oct 29); cream when unset. A dark one turns the header's words light. */
  colors: { ink: string; paper: string; accent: string; rule: string; header?: string }
  font_heading: string
  font_body: string
  header_style: 'masthead' | 'banner' | 'minimal'
  banner_url?: string | null
  logo_url?: string | null
  /** Your own banner's overlay (Brian's Brand studio: Overlay, Position,
   *  Align, Backing, Height). Migration 20240045. */
  banner_opts?: BannerOpts | null
}

export interface BannerOpts {
  /** "Issue & date" printed over the banner. */
  metaShow?: boolean
  metaPos?: 'top' | 'middle' | 'bottom'
  metaAlign?: 'left' | 'center' | 'right'
  /** light: a dark scrim under light text; dark: a cream plate under dark text. */
  metaInk?: 'light' | 'dark'
  /** 110 to 300px. */
  height?: number
}

/** Brian's header colours (BANNER_COLORS in after/newsletter.html), cream first. */
export const HEADER_COLORS: [string, string][] = [['Cream', '#f8f0e2'], ['Sand', '#efe3cf'], ['Sage', '#dfe8dc'], ['Sky', '#dde8f0'], ['Blush', '#f3e1dc'], ['Slate', '#34404d'], ['Navy', '#1f2f4a'], ['Forest', '#24382c'], ['Charcoal', '#2a2a2e']]
export const CREAM = '#f8f0e2'

/** Brian's BANNER_DARK: relative luminance under 0.45 takes light words. */
export function isDark(hex: string | null | undefined): boolean {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex ?? '')
  if (!m) return false
  const [r, g, b] = m.slice(1).map((x) => parseInt(x, 16) / 255)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.45
}

/**
 * The unsubscribe link in the footer. The send function (newsletter-send)
 * replaces it with each person's own signed link; the stored HTML, the test
 * hash and the preview all carry this placeholder.
 */
export const UNSUB_PLACEHOLDER = '{{{GG_UNSUBSCRIBE_URL}}}'

export const DEFAULT_BRAND: BrandKit = {
  name: 'House style',
  colors: { ink: '#2a2620', paper: '#ffffff', accent: '#a06530', rule: '#e8e6e0' },
  font_heading: 'Georgia',
  font_body: 'Arial',
  header_style: 'masthead',
}

export interface MagRow {
  photo: 'over' | 'left' | 'right'
  width: 1 | 2
  /** A locked ad in this row. A side ad restructures the row. */
  ad?: { shape: 'side'; side: 'left' | 'right' } | { shape: 'square' | 'leaderboard' | 'banner' } | null
}

export interface NewsletterConfig {
  storyIds: string[]
  noteOn: boolean
  note: string
  density: 'cozy' | 'compact'
  issueLabel: string
  dateLabel: string
  /** Bulletin: which slots between sections are locked as ad space. */
  /** Bulletin quick layout (Brian): Headline list, 2-up cards (pairs), 1 per
   *  row (stack), Section lead, Alternating. */
  bulletin: { preset: 'list' | 'lead' | 'pairs' | 'stack' | 'alt'; slots: number; locked: number[]; titles?: Record<string, string> }
  magazine: { rows: MagRow[] }
  /**
   * The creator's own photos, by story id. No story pictures go into the
   * newsletter (Brian, Oct 21: GoGrov stops short of the newsletter, so nobody
   * sends someone else's photo as their own); a story shows a picture only when
   * the creator adds one (Oct 29: "they should have the option on the newsletter
   * to add a photo; they may opt for just the article"), in every design.
   */
  photos?: Record<string, string>
  /** Banner details (Brian's builder): issue number and date, their colour and size. */
  meta?: { on?: boolean; color?: 'auto' | 'black' | 'white' | 'custom'; custom?: string; size?: number }
  issueAuto?: boolean
  issueNo?: number
  dateAuto?: boolean
  /** yyyy-mm-dd, when the date is Scheduled. */
  sendDate?: string
  /** Top of issue: the note in italic, and the "In this issue" list. */
  noteItalic?: boolean
  toc?: boolean
  /** The Letter's "Big lead story". */
  bigLead?: boolean
  /** A link to the original, by story id, over the story's own source link. */
  links?: Record<string, string>
  /** Stories the creator wrote themselves ("Write your own"). */
  own?: { id: string; headline: string; body: string }[]
  /** Rates typed in the builder for locked spots, by slot key, in dollars. */
  rates?: Record<string, number>
}

/** The creator's own stories as email stories. */
export function ownStories(config: NewsletterConfig): EmailStory[] {
  return (config.own ?? []).map((o) => ({
    id: o.id, headline: o.headline, summary: o.body || null, article: null, topic: 'civic', topicLabel: 'From me',
    source_url: null, source_name: null, place: null,
  }))
}

export const DEFAULT_CONFIG: NewsletterConfig = {
  storyIds: [],
  noteOn: true,
  note: '',
  density: 'cozy',
  issueLabel: 'Issue 1',
  dateLabel: '',
  bulletin: { preset: 'lead', slots: 2, locked: [] },
  magazine: { rows: [] },
}

export interface EmailStory {
  id: string
  headline: string
  summary: string | null
  article: string | null
  topic: string
  topicLabel: string
  source_url: string | null
  source_name: string | null
  place: string | null
}

/** What fills an ad slot at send time. */
export interface AdFill {
  slotKey: string
  shape: AdShape
  /** A booked creative. */
  imageUrl?: string | null
  sponsor?: string | null
  link?: string | null
  /** No booking: a house card, or (in the media kit) the price. */
  price?: string | null
}

export interface RenderInput {
  family: Family
  config: NewsletterConfig
  brand: BrandKit
  stories: EmailStory[]
  publication: string
  place: string
  ads: Record<string, AdFill>
  /** The media kit draws prices on open spots. */
  mode?: 'email' | 'kit'
  replyTo?: string | null
  /** The sender from Your list: the footer names them and prints the mailing
   *  address the law asks for (CAN-SPAM). */
  sender?: { fromName: string; address: string | null } | null
  /** The subject (the page title) and the preview text inboxes show after it. */
  subject?: string
  preheader?: string
}

const esc = (s: string | null | undefined) => (s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

function excerpt(s: EmailStory, sentences: number) {
  const src = (s.summary || s.article || '').replace(/\s+/g, ' ').trim()
  const parts = src.match(/[^.!?]+[.!?]+/g) ?? [src]
  return parts.slice(0, sentences).join(' ').trim()
}

/** Sections follow the stories' topics. A section can be renamed: the name is
 *  kept against the topic, so it survives stories coming and going. */
export function bulletinSections(stories: EmailStory[], titles?: Record<string, string>) {
  const map = new Map<string, EmailStory[]>()
  for (const s of stories) {
    const list = map.get(s.topicLabel) ?? []
    list.push(s)
    map.set(s.topicLabel, list)
  }
  return [...map.entries()].map(([key, list]) => ({ key, title: titles?.[key]?.trim() || key, stories: list }))
}

/** How many magazine rows the stories need, given each row's width and ads. */
export function magazineRows(config: NewsletterConfig, count: number): MagRow[] {
  const rows: MagRow[] = []
  let used = 0
  let i = 0
  while (used < count) {
    const r = config.magazine.rows[i] ?? { photo: 'over', width: 1, ad: null }
    rows.push(r)
    const takes = r.ad && r.ad.shape === 'side' ? 2 : r.width
    used += takes
    i++
    if (i > 40) break
  }
  return rows
}

export function renderEmail(input: RenderInput): string {
  const { family, config, brand, stories, publication, place } = input
  const c = brand.colors
  const hf = `${brand.font_heading}, Georgia, serif`
  const bf = `${brand.font_body}, Arial, sans-serif`
  const pad = config.density === 'compact' ? 16 : 24
  const gap = config.density === 'compact' ? 14 : 22

  const link = (s: EmailStory, label = 'Read more') => (config.links?.[s.id] || s.source_url)
    ? `<a href="${esc(config.links?.[s.id] || s.source_url)}" style="color:${c.accent};font-family:${bf};font-size:13px;font-weight:bold;text-decoration:none;">${label} &rsaquo;</a>`
    : ''
  const eyebrow = (t: string) => `<div style="font-family:${bf};font-size:11px;letter-spacing:2px;text-transform:uppercase;color:${c.accent};margin:0 0 6px;">${esc(t)}</div>`
  const h = (t: string, size: number) => `<div style="font-family:${hf};font-size:${size}px;line-height:1.2;color:${c.ink};margin:0 0 8px;">${esc(t)}</div>`
  const p = (t: string) => t ? `<div style="font-family:${bf};font-size:15px;line-height:1.6;color:${c.ink};opacity:.86;margin:0 0 10px;">${esc(t)}</div>` : ''
  // A story's picture is only ever the creator's own (config.photos).
  const photo = (st: EmailStory): string | null => config.photos?.[st.id] ?? null
  const img = (url: string | null, w: number, hgt: number, alt = '') => url
    ? `<img src="${esc(url)}" width="${w}" height="${hgt}" alt="${esc(alt)}" style="display:block;width:100%;max-width:${w}px;height:auto;border:0;border-radius:6px;" />`
    : ''
  const rule = `<tr><td style="padding:0 ${pad}px;"><div style="height:1px;background:${c.rule};line-height:1px;font-size:1px;">&nbsp;</div></td></tr>`

  const ad = (key: string, shape: AdShape) => {
    const fill = input.ads[key]
    const { w, h: hh } = SHAPES[shape]
    if (fill?.imageUrl) {
      const im = `<img src="${esc(fill.imageUrl)}" width="${w}" height="${hh}" alt="${esc(fill.sponsor ?? 'Sponsor')}" style="display:block;width:100%;max-width:${w}px;height:${hh}px;object-fit:cover;border:0;" />`
      return `<div style="font-family:${bf};font-size:9px;letter-spacing:1px;text-transform:uppercase;color:#999;margin:0 0 4px;">Sponsored${fill.sponsor ? ` by ${esc(fill.sponsor)}` : ''}</div>${fill.link ? `<a href="${esc(fill.link)}">${im}</a>` : im}`
    }
    const kit = input.mode === 'kit'
    const label = kit ? (fill?.price ? `Open · ${esc(fill.price)}` : 'Open') : 'Your business here'
    const sub = kit ? SHAPES[shape].label : (input.replyTo ? `Reply to sponsor this spot` : 'Sponsor this newsletter')
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:2px dashed ${c.accent};border-radius:6px;"><tr><td height="${Math.min(hh, 150)}" align="center" valign="middle" style="font-family:${bf};color:${c.accent};padding:10px;">
      <div style="font-size:15px;font-weight:bold;">${label}</div><div style="font-size:11px;opacity:.8;margin-top:4px;">${esc(sub)}</div></td></tr></table>`
  }

  // ---------- header ----------
  // Banner details: off prints no issue number or date; the colour and size
  // are the creator's (Auto keeps the accent).
  const m = config.meta ?? {}
  const metaOn = m.on !== false
  // The masthead's colour (cream unless the kit picks another); a dark one
  // takes light words, in the builder and in the email alike.
  const headBg = c.header || CREAM
  const darkHead = brand.header_style === 'masthead' && isDark(headBg)
  const metaColor = m.color === 'black' ? '#1d1d1f' : m.color === 'white' ? '#ffffff' : m.color === 'custom' && m.custom ? m.custom : darkHead ? '#e9e3d6' : c.accent
  const metaSize = m.size ?? 10
  const metaCss = `font-family:${bf};font-size:${metaSize}px;letter-spacing:2px;text-transform:uppercase;color:${metaColor};`
  const issueDate = metaOn ? [config.issueLabel, config.dateLabel].filter(Boolean).map((x) => esc(x)).join(' &middot; ') : ''
  let header = ''
  if (brand.header_style === 'banner' && brand.banner_url) {
    // Your own banner replaces the name and tagline block; the issue and date
    // sit over it, where Brand studio puts them.
    const o = brand.banner_opts ?? {}
    const hh = Math.max(110, Math.min(300, o.height ?? 150))
    const valign = o.metaPos === 'top' ? 'top' : o.metaPos === 'middle' ? 'middle' : 'bottom'
    const align = o.metaAlign === 'left' ? 'left' : o.metaAlign === 'right' ? 'right' : 'center'
    const plate = o.metaInk === 'dark' ? 'background:#f8f0e2;color:#2a2620;' : 'background:rgba(20,16,10,.55);color:#fffdf7;'
    const over = issueDate && o.metaShow !== false
      ? `<span style="display:inline-block;${metaCss}${plate}${m.color && m.color !== 'auto' ? `color:${metaColor};` : ''}padding:5px 10px;border-radius:3px;">${issueDate}</span>` : '&nbsp;'
    header = `<tr><td background="${esc(brand.banner_url)}" height="${hh}" valign="${valign}" align="${align}" style="height:${hh}px;padding:14px ${pad}px;background-image:url('${esc(brand.banner_url)}');background-size:cover;background-position:center;background-color:${c.rule};">${over}</td></tr>`
  } else if (brand.header_style === 'minimal') {
    header = `<tr><td style="padding:${pad}px ${pad}px 8px;font-family:${hf};font-size:22px;color:${c.ink};">${esc(publication)}
      ${metaOn ? `<span style="${metaCss}float:right;margin-top:8px;">${esc(config.dateLabel || config.issueLabel)}</span>` : ''}</td></tr>`
  } else {
    header = `<tr><td align="center" style="padding:${pad + 6}px ${pad}px ${pad - 6}px;background-color:${headBg};">
      ${brand.logo_url ? `<img src="${esc(brand.logo_url)}" height="44" alt="${esc(publication)}" style="display:block;margin:0 auto 10px;height:44px;border:0;" />` : ''}
      <div style="font-family:${hf};font-size:34px;line-height:1.1;color:${darkHead ? '#fffdf7' : c.ink};">${esc(publication)}</div>
      <div style="${metaCss}margin-top:8px;">${[esc(place), issueDate].filter(Boolean).join(' &middot; ')}</div>
    </td></tr>${darkHead ? '' : rule}`
  }

  const note = config.noteOn && config.note.trim()
    ? `<tr><td style="padding:${pad}px ${pad}px 0;">${eyebrow('A note from me')}<div style="font-family:${hf};font-size:17px;line-height:1.6;color:${c.ink};font-style:${config.noteItalic === false ? 'normal' : 'italic'};">${esc(config.note).replace(/\n/g, '<br/>')}</div></td></tr>`
    : ''
  // Top of issue: "In this issue", headlines only (jump links don't work in Gmail).
  const toc = family !== 'letter' && config.toc !== false && stories.length
    ? `<tr><td style="padding:${pad}px ${pad}px 0;">${eyebrow('In this issue')}${stories.map((s, i) =>
      `<div style="font-family:${bf};font-size:13px;line-height:1.5;color:${c.ink};padding:2px 0;"><span style="color:${c.accent};font-family:Courier New,monospace;font-size:12px;">${String(i + 1).padStart(2, '0')}</span>&nbsp; ${esc(s.headline)}</div>`).join('')}</td></tr>${rule}`
    : ''

  // ---------- body ----------
  let body = ''
  if (family === 'letter') {
    body = stories.slice(0, 6).map((s, i) => `
      <tr><td style="padding:${i === 0 ? pad : gap}px ${pad}px 0;">
        ${eyebrow(s.topicLabel + (s.place ? ` · ${s.place}` : ''))}${photo(s) ? `${img(photo(s), 552, 260, s.headline)}<div style="height:12px"></div>` : ''}${h(s.headline, config.bigLead && i === 0 ? 28 : 22)}${p(excerpt(s, config.bigLead && i === 0 ? 5 : 3))}${link(s)}
      </td></tr>`).join(rule)
  } else if (family === 'bulletin') {
    const sections = bulletinSections(stories, config.bulletin.titles)
    const slots = Math.max(2, Math.min(6, config.bulletin.slots))
    body = sections.map((sec, si) => {
      const [lead, ...rest] = sec.stories
      const items = config.bulletin.preset === 'lead' && lead
        ? `<tr><td style="padding:0 ${pad}px ${gap}px;">${img(photo(lead), 552, 300, lead.headline)}<div style="height:10px"></div>${h(lead.headline, 21)}${p(excerpt(lead, 2))}${link(lead)}</td></tr>` +
          rest.map((s) => `<tr><td style="padding:0 ${pad}px ${gap - 6}px;">${photo(s) ? `${img(photo(s), 552, 160, s.headline)}<div style="height:8px"></div>` : ''}${h(s.headline, 17)}${p(excerpt(s, 1))}${link(s)}</td></tr>`).join('')
        : config.bulletin.preset === 'pairs'
          ? pairRows(sec.stories, (s) => `${img(photo(s), 264, 160, s.headline)}<div style="height:8px"></div>${h(s.headline, 16)}${p(excerpt(s, 1))}${link(s)}`, pad)
          : config.bulletin.preset === 'stack'
            ? sec.stories.map((s) => `<tr><td style="padding:0 ${pad}px ${gap}px;">${img(photo(s), 552, 300, s.headline)}${photo(s) ? '<div style="height:10px"></div>' : ''}${h(s.headline, 19)}${p(excerpt(s, 2))}${link(s)}</td></tr>`).join('')
            : config.bulletin.preset === 'alt'
              ? sec.stories.map((s, k) => {
                if (!photo(s)) return `<tr><td style="padding:0 ${pad}px ${gap}px;">${h(s.headline, 18)}${p(excerpt(s, 2))}${link(s)}</td></tr>`
                const pic = `<td width="40%" valign="top">${img(photo(s), 220, 160, s.headline)}</td>`
                const txt = `<td width="56%" valign="top">${h(s.headline, 18)}${p(excerpt(s, 2))}${link(s)}</td>`
                return `<tr><td style="padding:0 ${pad}px ${gap}px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>${k % 2 === 0 ? pic + '<td width="4%"></td>' + txt : txt + '<td width="4%"></td>' + pic}</tr></table></td></tr>`
              }).join('')
          : sec.stories.map((s) => `<tr><td style="padding:0 ${pad}px ${gap - 6}px;">${photo(s) ? `${img(photo(s), 552, 160, s.headline)}<div style="height:8px"></div>` : ''}${h(s.headline, 17)}${p(excerpt(s, 2))}${link(s)}</td></tr>`).join('')
      const adRow = si < slots && config.bulletin.locked.includes(si)
        ? `<tr><td style="padding:0 0 ${gap}px;">${ad(`bulletin-${si}`, 'banner')}</td></tr>` : ''
      return `<tr><td style="padding:${gap}px ${pad}px 10px;"><div style="font-family:${bf};font-size:12px;letter-spacing:3px;text-transform:uppercase;color:${c.ink};border-bottom:2px solid ${c.ink};padding-bottom:6px;">${esc(sec.title)}</div></td></tr>${items}${adRow}`
    }).join('') + config.bulletin.locked
      .filter((i) => i >= sections.length && i < slots)
      .map((i) => `<tr><td style="padding:${gap}px 0 0;">${ad(`bulletin-${i}`, 'banner')}</td></tr>`).join('')
  } else {
    const rows = magazineRows(config, stories.length)
    let q = 0
    body = rows.map((r, ri) => {
      const take = () => stories[q++]
      const side = r.ad && r.ad.shape === 'side' ? r.ad.side : null
      if (side) {
        const a = take(); const b = take()
        const long = a ? `${img(photo(a), 272, 180, a.headline)}<div style="height:8px"></div>${h(a.headline, 20)}${p(excerpt(a, 4))}${link(a)}` : ''
        const under = b ? `<div style="height:${gap}px"></div>${img(photo(b), 272, 150, b.headline)}<div style="height:8px"></div>${h(b.headline, 16)}${p(excerpt(b, 1))}${link(b)}` : ''
        const adCol = `${ad(`mag-${ri}`, 'side')}${under}`
        const left = side === 'left' ? adCol : long
        const right = side === 'left' ? long : adCol
        return `<tr><td style="padding:${gap}px ${pad}px 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
          <td width="48%" valign="top">${left}</td><td width="4%"></td><td width="48%" valign="top">${right}</td></tr></table></td></tr>`
      }
      let html = ''
      if (r.width === 2) {
        const a = take(); const b = take()
        html = pairRows([a, b].filter(Boolean) as EmailStory[], (s) => `${img(photo(s), 264, 160, s.headline)}<div style="height:8px"></div>${h(s.headline, 17)}${p(excerpt(s, 2))}${link(s)}`, pad, gap)
      } else {
        const s = take()
        if (s) {
          if (r.photo === 'over' || !photo(s)) {
            html = `<tr><td style="padding:${gap}px ${pad}px 0;">${img(photo(s), 552, 300, s.headline)}${photo(s) ? '<div style="height:12px"></div>' : ''}${eyebrow(s.topicLabel)}${h(s.headline, 24)}${p(excerpt(s, 3))}${link(s)}</td></tr>`
          } else {
            const pic = `<td width="40%" valign="top">${img(photo(s), 220, 170, s.headline)}</td>`
            const txt = `<td width="56%" valign="top">${eyebrow(s.topicLabel)}${h(s.headline, 20)}${p(excerpt(s, 2))}${link(s)}</td>`
            html = `<tr><td style="padding:${gap}px ${pad}px 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
              ${r.photo === 'left' ? pic + '<td width="4%"></td>' + txt : txt + '<td width="4%"></td>' + pic}</tr></table></td></tr>`
          }
        }
      }
      const extra = r.ad && r.ad.shape !== 'side'
        ? `<tr><td align="center" style="padding:${gap}px ${r.ad.shape === 'square' ? pad : 0}px 0;">${ad(`mag-${ri}`, r.ad.shape)}</td></tr>` : ''
      return html + extra
    }).join('')
  }

  // Brian's nl-send.js: the email asks for light (Apple Mail honours it; the
  // Gmail and Outlook apps flip it anyway, which Preview's Dark switch shows).
  const pre = input.preheader?.trim()
    ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#ffffff;opacity:0;">${esc(input.preheader.trim())}${'&#847;&zwnj;&nbsp;'.repeat(30)}</div>` : ''
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="x-apple-disable-message-reformatting"><meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light"><title>${esc(input.subject?.trim() || publication)}</title></head>
<body style="margin:0;padding:0;background:#f1efea;">${pre}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f1efea;"><tr><td align="center" style="padding:20px 0;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;background:${c.paper};">
${header}${note}${toc}${body}
${foot(input, pad, c.rule)}
</table></td></tr></table></body></html>`
}

/** The foot (Brian's nl-send.js): who it's from, the unsubscribe link and the
 *  mailing address. GoGrov adds both to every issue, as the law requires. The
 *  media kit shows the issue without them. */
function foot(input: RenderInput, pad: number, rule: string) {
  if (input.mode === 'kit') return ''
  const who = input.sender?.fromName || input.publication || 'this newsletter'
  const a = 'color:#98948a;text-decoration:underline;'
  return `<tr><td align="center" style="padding:${pad + 8}px ${pad}px ${pad}px;">
  <div style="height:1px;background:${rule};margin-bottom:14px;"></div>
  <div style="font-family:Helvetica,Arial,sans-serif;font-size:12px;line-height:1.6;color:#98948a;">You’re getting this because you subscribed to ${esc(who)}.<br><a href="${UNSUB_PLACEHOLDER}" style="${a}">Unsubscribe</a>${input.sender?.address ? `<br>${esc(input.sender.address)}` : ''}</div>
</td></tr>`
}

/** The plain-text part (some readers and spam filters want one): Brian's GGSend.text(). */
export function renderText(input: RenderInput): string {
  const { config, stories, publication, place } = input
  const L: string[] = [publication, place, [config.issueLabel, config.dateLabel].filter(Boolean).join(' · '), '']
  if (config.noteOn && config.note.trim()) L.push(config.note.trim(), '')
  stories.forEach((s) => {
    L.push(s.headline)
    const ex = excerpt(s, 3)
    if (ex) L.push(ex)
    const href = config.links?.[s.id] || s.source_url
    if (href) L.push(`Read more: ${href}`)
    L.push('')
  })
  L.push('--', `You’re getting this because you subscribed to ${input.sender?.fromName || publication}.`, `Unsubscribe: ${UNSUB_PLACEHOLDER}`)
  if (input.sender?.address) L.push(input.sender.address)
  return L.join('\n')
}

/** What an inbox would break (Brian's GGSend.check()). Empty means ready. */
export function inboxCheck(html: string): string[] {
  const p: string[] = []
  if (/var\(--/.test(html)) p.push('A CSS variable is left in the HTML (Gmail drops them).')
  if (/display:\s*(flex|grid)/.test(html)) p.push('Flex or grid layout is left in the HTML (Outlook ignores it).')
  if (/background-image/.test(html)) p.push('Your banner is a background image. Gmail and Apple Mail show it; Outlook on a computer shows its colour instead.')
  const imgs = [...html.matchAll(/<img[^>]+src="([^"]+)"/g)].map((m) => m[1])
  const local = imgs.filter((s) => !/^https:\/\//.test(s))
  if (local.length) p.push(`${local.length} image${local.length === 1 ? '' : 's'} not on an https address yet, so inboxes can't load ${local.length === 1 ? 'it' : 'them'}.`)
  if (!html.includes(UNSUB_PLACEHOLDER)) p.push('There is no unsubscribe link.')
  const kb = Math.round(new Blob([html]).size / 1024)
  if (kb > 100) p.push(`The email is ${kb} KB; Gmail clips anything over about 100 KB (“Message clipped”).`)
  return p
}

function pairRows(items: EmailStory[], cell: (s: EmailStory) => string, pad: number, gap = 18) {
  let out = ''
  for (let i = 0; i < items.length; i += 2) {
    const a = items[i], b = items[i + 1]
    out += `<tr><td style="padding:${gap}px ${pad}px 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
      <td width="48%" valign="top">${cell(a)}</td><td width="4%"></td><td width="48%" valign="top">${b ? cell(b) : ''}</td></tr></table></td></tr>`
  }
  return out
}

/** The Bulletin's sponsor sections, in order (Brian's deskSpotDefs). */
export const BULLETIN_SPOTS = ['Local news sponsor', 'Events sponsor', 'Market sponsor', 'Second news sponsor', 'Second events sponsor', 'Closing sponsor']

/** A spot's place in the issue and its size, as the Sponsor desk shows them. */
export function spotPlace(key: string, shape: AdShape): { place: string; dim: string } {
  const n = Number(key.split('-')[1] ?? 0) + 1
  return { place: key.startsWith('bulletin-') ? `Section ${n} banner` : `Row ${n} · position held`, dim: `${SHAPES[shape].w}×${SHAPES[shape].h}` }
}

/** Every ad slot a design locks, with its key and shape. */
export function lockedSlots(family: Family, config: NewsletterConfig, storyCount: number): { key: string; shape: AdShape; label: string }[] {
  if (family === 'letter') return []
  if (family === 'bulletin') {
    return config.bulletin.locked.map((i) => ({ key: `bulletin-${i}`, shape: 'banner' as AdShape, label: BULLETIN_SPOTS[i] ?? `Section ${i + 1} sponsor` }))
  }
  return magazineRows(config, Math.max(storyCount, 4)).flatMap((r, i) => r.ad
    ? [{ key: `mag-${i}`, shape: r.ad.shape as AdShape, label: r.ad.shape === 'side' ? `Locked ${r.ad.side} ad` : r.ad.shape === 'banner' ? 'Locked banner' : `Locked ${r.ad.shape}` }]
    : [])
}

/** How an uploaded creative fits a slot: the crop it will take and whether it
 *  is too small. Stated, not hidden. */
export function creativeFit(w: number, h: number, shape: AdShape) {
  const t = SHAPES[shape]
  const target = t.w / t.h
  const ratio = w / h
  let crop = { x: 0, y: 0, w, h }
  let lost = 0
  if (ratio > target) {
    const nw = Math.round(h * target); crop = { x: Math.round((w - nw) / 2), y: 0, w: nw, h }; lost = 1 - nw / w
  } else if (ratio < target) {
    const nh = Math.round(w / target); crop = { x: 0, y: Math.round((h - nh) / 2), w, h: nh }; lost = 1 - nh / h
  }
  const pct = Math.round(lost * 100)
  const best = (Object.keys(SHAPES) as AdShape[]).reduce((b, s) =>
    Math.abs(Math.log((SHAPES[s].w / SHAPES[s].h) / ratio)) < Math.abs(Math.log((SHAPES[b].w / SHAPES[b].h) / ratio)) ? s : b, 'banner' as AdShape)
  const warnings: string[] = []
  if (pct >= 3) warnings.push(`Doesn't match the ${t.label} shape. It will be cropped ${pct}% to fit.`)
  if (w < t.w) warnings.push(`It is ${w}px wide, under the ${t.w}px this spot shows at, so it may look soft.`)
  return { crop, pct, best, warnings }
}
